/**
 * SettingsPage — Финальная версия с исправлением типов
 */

import { useState, useEffect } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { getBotConfig, updateBotConfig } from '../utils/api';
import type { BotConfig, Service } from '@tg-miniapp/shared-types';

export function SettingsPage(): React.JSX.Element {
  const { tg, initData } = useTelegram();
  const [config, setConfig] = useState<BotConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>('info');

  useEffect(() => {
    async function load() {
      const res = await getBotConfig(initData || '');
      if (res.ok) setConfig(res.value);
      setLoading(false);
    }
    load();
  }, [initData]);

  useEffect(() => {
    if (!config || !tg) return;
    
    tg.MainButton.setText('Сохранить изменения');
    tg.MainButton.show();
    
    const onSave = async () => {
      const res = await updateBotConfig(initData, config);
      if (res.ok) {
        tg.HapticFeedback.notificationOccurred('success');
        tg.showAlert('Сохранено! ✅');
      } else {
        tg.showAlert('Ошибка: ' + res.error.message);
      }
    };

    tg.MainButton.onClick(onSave);
    return () => {
      tg.MainButton.offClick(onSave);
      tg.MainButton.hide();
    };
  }, [config, initData, tg]);

  if (loading) return <div className="flex h-screen items-center justify-center text-xl">✨ Загрузка...</div>;
  if (!config) return <div className="p-10 text-center text-[var(--tg-theme-text-color)]">Ошибка загрузки</div>;

  const toggle = (key: string) => setOpen(open === key ? null : key);

  return (
    <div className="px-5 pt-8 pb-32">
      <h1 className="text-3xl font-bold text-[var(--tg-theme-text-color)]">⚙️ Настройки</h1>

      {/* О бизнесе */}
      <Section title="О бизнесе" icon="🏢" isOpen={open === 'info'} onToggle={() => toggle('info')}>
        <div className="space-y-4 pt-2">
          <Input 
            label="Название вашего бизнеса" 
            val={config.businessName} 
            onChange={(v: string) => setConfig({ ...config, businessName: v })} 
          />
        </div>
      </Section>

      {/* Услуги */}
      <Section title="Ваши услуги" icon="💈" isOpen={open === 'svc'} onToggle={() => toggle('svc')}>
        <div className="space-y-4 pt-2">
          {config.services.map((s, idx) => (
            <div key={idx} className="card-premium !bg-[var(--tg-theme-bg-color)] border-2 border-[var(--tg-theme-button-color)]/20">
              <div className="flex justify-between items-start mb-4">
                 <input 
                   className="bg-transparent font-bold text-lg outline-none w-full text-[var(--tg-theme-text-color)]"
                   value={s.name}
                   onChange={(e) => {
                      const newServices = [...config.services];
                      newServices[idx] = { ...newServices[idx], name: e.target.value } as Service;
                      setConfig({ ...config, services: newServices });
                   }}
                 />
                 <button 
                   onClick={() => {
                      const newServices = config.services.filter((_, i) => i !== idx);
                      setConfig({ ...config, services: newServices });
                   }}
                   className="p-2 text-red-500 text-lg"
                 >✕</button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="text-[10px] font-bold uppercase text-[var(--tg-theme-hint-color)]">Цена (₽)</label>
                   <input 
                      type="number"
                      className="w-full bg-transparent text-xl font-medium outline-none border-b-2 border-[var(--tg-theme-hint-color)]/20 py-1 text-[var(--tg-theme-text-color)]"
                      value={s.price}
                      onChange={(e) => {
                        const newServices = [...config.services];
                        newServices[idx] = { ...newServices[idx], price: Number(e.target.value) } as Service;
                        setConfig({ ...config, services: newServices });
                      }}
                   />
                 </div>
                 <div>
                   <label className="text-[10px] font-bold uppercase text-[var(--tg-theme-hint-color)]">Мин</label>
                   <input 
                      type="number"
                      className="w-full bg-transparent text-xl font-medium outline-none border-b-2 border-[var(--tg-theme-hint-color)]/20 py-1 text-[var(--tg-theme-text-color)]"
                      value={s.durationMinutes}
                      onChange={(e) => {
                        const newServices = [...config.services];
                        newServices[idx] = { ...newServices[idx], durationMinutes: Number(e.target.value) } as Service;
                        setConfig({ ...config, services: newServices });
                      }}
                   />
                 </div>
              </div>
            </div>
          ))}
          <button 
            onClick={() => {
              const newSvc: Service = { 
                id: Math.random().toString(36), 
                name: 'Новая услуга', 
                price: 1000, 
                durationMinutes: 60,
                category: null 
              };
              setConfig({ ...config, services: [...config.services, newSvc] });
            }}
            className="w-full rounded-2xl border-2 border-dashed border-[var(--tg-theme-hint-color)]/30 py-5 text-lg font-semibold text-[var(--tg-theme-button-color)] active:scale-95 transition-transform"
          >
            + Добавить услугу
          </button>
        </div>
      </Section>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────

function Section({ title, icon, isOpen, onToggle, children }: any) {
  return (
    <div className="card-premium mb-4 !p-0 overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center gap-4 p-5 text-left active:bg-black/5">
        <span className="text-3xl">{icon}</span>
        <div className="flex-1">
          <div className="font-bold text-xl text-[var(--tg-theme-text-color)]">{title}</div>
        </div>
        <span className={`text-2xl transition-transform duration-300 text-[var(--tg-theme-hint-color)] ${isOpen ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {isOpen && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

function Input({ label, val, onChange }: any) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase text-[var(--tg-theme-hint-color)] mb-2 ml-1">{label}</label>
      <input 
        type="text" 
        value={val} 
        onChange={(e) => onChange(e.target.value)}
        className="input-premium !text-xl !py-4" 
      />
    </div>
  );
}
